import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { UploadCloud, Link2, Loader2, Mic } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { chunkedUpload, ingestUrl } from "../lib/api";
import { toast } from "sonner";

export const UploadPanel = ({ onCreated }) => {
  const [mode, setMode] = useState("upload");
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [showDate, setShowDate] = useState("");
  const inputRef = useRef(null);

  const doUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    setUploadPct(0);
    try {
      const show = await chunkedUpload(file, title, (p) => setUploadPct(p), showDate);
      toast.success("Show uploaded — mining started");
      setTitle("");
      setShowDate("");
      onCreated(show);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      setUploadPct(0);
    }
  };

  const doUrl = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      const show = await ingestUrl({ url: url.trim(), title: title || null, show_date: showDate || null });
      toast.success("Link queued — mining started");
      setUrl("");
      setTitle("");
      setShowDate("");
      onCreated(show);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to ingest URL");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="upload-panel" className="border border-border bg-card/60 backdrop-blur-sm">
      <div className="flex items-stretch border-b border-border">
        <button
          data-testid="mode-upload-tab"
          onClick={() => setMode("upload")}
          className={`flex-1 px-6 py-4 text-xs tracking-[0.2em] uppercase font-semibold transition-colors ${
            mode === "upload" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-white"
          }`}
        >
          <UploadCloud className="inline mr-2 h-4 w-4" /> Upload File
        </button>
        <button
          data-testid="mode-url-tab"
          onClick={() => setMode("url")}
          className={`flex-1 px-6 py-4 text-xs tracking-[0.2em] uppercase font-semibold transition-colors ${
            mode === "url" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-white"
          }`}
        >
          <Link2 className="inline mr-2 h-4 w-4" /> From URL
        </button>
      </div>

      <div className="p-6 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Show Title (optional)</label>
            <Input
              data-testid="show-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Late Night with Barth & Friends"
              className="mt-2 rounded-none bg-background border-border focus-visible:ring-1 focus-visible:ring-accent"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Show Date (optional)</label>
            <Input
              data-testid="show-date-input"
              type="date"
              value={showDate}
              onChange={(e) => setShowDate(e.target.value)}
              className="mt-2 rounded-none bg-background border-border focus-visible:ring-1 focus-visible:ring-accent"
            />
            <p className="mt-1 text-[10px] text-muted-foreground font-mono">The date it aired — shows on the episode card.</p>
          </div>
        </div>

        {mode === "upload" ? (
          <div
            data-testid="dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              doUpload(e.dataTransfer.files?.[0]);
            }}
            onClick={() => !busy && inputRef.current?.click()}
            className={`relative cursor-pointer border border-dashed p-10 text-center transition-colors ${
              dragging ? "border-accent bg-accent/5" : "border-border hover:border-primary"
            }`}
          >
            <input
              ref={inputRef}
              type="file"
              accept="audio/*,video/*,.mp3,.wav,.m4a,.aac,.ogg,.flac,.mp4,.mov,.webm"
              className="hidden"
              data-testid="file-input"
              onChange={(e) => doUpload(e.target.files?.[0])}
            />
            {busy ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
                <p className="font-mono text-sm">{uploadPct}% uploaded</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="border border-border p-4">
                  <Mic className="h-7 w-7 text-primary" />
                </div>
                <p className="font-body text-sm">
                  Drop your full show here, or <span className="text-accent">browse</span>
                </p>
                <p className="text-xs text-muted-foreground font-mono">MP3 · WAV · M4A · MP4 · MOV — up to 8GB / 4h+ shows</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <Input
              data-testid="url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste a Dropbox or Google Drive share link"
              className="rounded-none bg-background border-border font-mono text-sm focus-visible:ring-1 focus-visible:ring-accent"
            />
            <p className="text-xs text-muted-foreground font-mono">
              Best for 4h+ / multi-GB shows — paste a Dropbox or Google Drive share link (set to "anyone with the link").
            </p>
            <motion.div whileTap={{ scale: 0.98 }}>
              <Button
                data-testid="ingest-url-btn"
                onClick={doUrl}
                disabled={busy || !url.trim()}
                className="w-full rounded-none bg-accent text-accent-foreground hover:bg-accent/90 font-semibold tracking-[0.15em] uppercase text-xs"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mine This Link"}
              </Button>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};
